import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { IAclID } from 'src/app/acl-data/aclID.interface';
import { Alert, Document } from 'src/app/acl-data/document/document.dto';
import { DocumentService } from 'src/app/acl-data/document/document.service';
import { FolderService } from 'src/app/acl-data/folder/folder.service';
import { IBookmarkEntry } from 'src/app/navigation/menuInterfaces/bookmark-entry.interface';
import { TopnavInfoService } from 'src/app/navigation/menuInterfaces/topnav-info.service';
import { SubSink } from 'subsink';
import { ObjectStoreService } from 'src/app/acl-data/object-store/object-store.service';
import { deepCopy } from 'src/app/acl-data/general/generic.function';

export enum VIEW_SELECTED_TAB {
    Properties = 0,
    Comments = 1,
    LinkedDocuments = 2,
    EventNotifications = 3,
    SystemAlerts = 4,
    Document = 5,
    Security = 6,
    AuditTrail = 7,
}

@Component({
    templateUrl: './document-view.component.html',
    styleUrls: ['./document-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentViewComponent implements OnDestroy, OnInit {
    subSink = new SubSink();
    objectStore: string;
    doc: Document;
    @ViewChild('tabs', { static: true }) tabs: MatTabGroup;
    documentID$ = combineLatest([
        this.route.paramMap,
        this.documentService.entities$,
    ]).pipe(
        map(([v, _]) => v.get('documentID')),
        withLatestFrom(this.route.data),
        // tap(v => console.log('v', v)),
        map(([idValue, routeValue]) => {
            return {
                id: idValue,
                objectStore: routeValue.parentMenu.data.id,
            } as IAclID;
        })
    );
    document$ = this.documentID$.pipe(
        switchMap(v => this.documentService.getByID(v.objectStore, v.id, true))
    );
    comments$ = this.document$.pipe(
        map(v => v.annotations),
        map(v => {
            try {
                return v.sort((a, b) =>
                    a.propertiesObj.AnnotationDate <
                    b.propertiesObj.AnnotationDate
                        ? 1
                        : -1
                );
            } catch (e) {
                // TODO: Fix this shit
                return v;
            }
        })
    );

    userRoles$ = this.objectStoreService.entities$.pipe(
        withLatestFrom(this.documentID$),
        map(([v, d]) => v.find(row => row.symbolicName === d.objectStore))
        // tap(v => console.log('Role', v))
    );

    linkedDocuments$ = combineLatest([this.document$, this.documentID$]).pipe(
        // tap(v => console.log('Test', v)),
        switchMap(([doc, id]) =>
            this.documentService
                .getByIds(id.objectStore, doc.linkedDocuments, true)
                .pipe(map(v => v.map(linkedDoc => linkedDoc.id)))
        )
    );
    loading$ = this.documentService.loading$;
    currentTab$ = this.route.queryParams.pipe(
        map(v => v.currentTab),
        filter(v => !!v)
    );
    notificationChanged(alert: Alert, objectStore: string, documentID: string) {
        const alertObj = { ...alert };
        alertObj.docId = documentID;
        console.log('Alert Changed in document view', documentID, alertObj);
        this.documentService
            .updateAlert(objectStore, documentID, alertObj)
            .subscribe(v => {
                return;
            });
    }
    ngOnInit() {
        this.subSink.add(
            this.document$.subscribe(v => {
                this.doc = deepCopy(v);
            })
        );
        this.subSink.add(
            combineLatest([this.document$, this.currentTab$]).subscribe(
                ([_, v]) => {
                    if (!!this.tabs) {
                        if (this.tabs.selectedIndex !== v) {
                            const tabValue = (VIEW_SELECTED_TAB[
                                v
                            ] as unknown) as number;
                            this.tabs.selectedIndex = tabValue;
                        }
                    }
                }
            )
        );
    }
    ngOnDestroy(): void {
        this.subSink.unsubscribe();
    }
    tabClicked = (event: MatTabChangeEvent) => {
        const selectIndex = VIEW_SELECTED_TAB[event.index];
        // console.log('Navigating: tabClicked');
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { currentTab: selectIndex },
            queryParamsHandling: 'merge',
        });
    };
    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private documentService: DocumentService,
        private topNavInfoService: TopnavInfoService,
        private folderService: FolderService,
        private objectStoreService: ObjectStoreService
    ) {
        this.subSink.add(
            combineLatest([
                this.route.paramMap,
                this.route.data,
                this.document$,
            ])
                .pipe(
                    switchMap(([parmMap, routeValue]) =>
                        this.documentService
                            .displayById(parmMap.get('documentID'))
                            .pipe(
                                // Document for Breadcrumb
                                filter(v => !!v),
                                switchMap(doc =>
                                    this.folderService
                                        .getByID(
                                            // Category For breadcrumb
                                            routeValue.parentMenu.data.id,
                                            doc.propertiesObj.FoldersFiledInID
                                        )
                                        .pipe(
                                            filter(v => !!v),
                                            switchMap(folderCategory =>
                                                this.folderService
                                                    .getByID(
                                                        // Library For breadcrumb
                                                        routeValue.parentMenu
                                                            .data.id,
                                                        folderCategory.parentID
                                                    )
                                                    .pipe(
                                                        filter(v => !!v),
                                                        tap(folderLibrary => {
                                                            this.objectStore =
                                                                routeValue.parentMenu.data.id;
                                                            if (!doc) {
                                                                return;
                                                            }
                                                            const result: IBookmarkEntry[] = [];
                                                            result.push({
                                                                path: `/${this.objectStore}/browse/${folderLibrary.id}`,
                                                                description:
                                                                    folderLibrary
                                                                        .propertiesObj
                                                                        .FolderName,
                                                            });
                                                            this.topNavInfoService.addExtraBreadCrumbData(
                                                                result
                                                            );
                                                            result.push({
                                                                path: `/${this.objectStore}/browse/${folderCategory.id}`,
                                                                description:
                                                                    folderCategory
                                                                        .propertiesObj
                                                                        .FolderName,
                                                            });
                                                            this.topNavInfoService.addExtraBreadCrumbData(
                                                                result
                                                            );
                                                            result.push({
                                                                path: `/${this.objectStore}/browse/${doc.id}`,
                                                                description:
                                                                    doc
                                                                        .propertiesObj
                                                                        .DocumentTitle,
                                                            });
                                                            this.topNavInfoService.addExtraBreadCrumbData(
                                                                result
                                                            );
                                                        })
                                                    )
                                            )
                                        )
                                )
                            )
                    )
                )
                .subscribe(v => {
                    return;
                })
        );
    }
}
